import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import PhoneDisabledOutlinedIcon from '@mui/icons-material/PhoneDisabledOutlined';

import { describeBatchError, forgetBatchData } from '../batch/batchUpdate';
import { CRM_PATHS } from '../crmPaths';
import { CRM_SET_CONTACT_FLAG } from './contactDetail.queries';
import {
  ContactDetail,
  describeFlagOrigin,
  FLAG_WORDS,
  FlagField,
  flagChange,
  flagNeedsNote,
  shownFlags,
} from './contactDetail';

/**
 * Do not call and do not contact (card 20.5): a banner, not a checkbox.
 *
 * - **The banner stays in view.** It is sticky at the top of the page's scroll
 *   area (DashboardRoutes' `Main`), so it is still there while the timeline
 *   scrolls under it.
 * - **It says who, when and why** (`doNotCallSet`): a call where they asked, a
 *   batch update, its undo, or NationBuilder.
 * - **Marking and lifting are batch updates of one person** (card 20), so each
 *   is on their timeline, in the banner, and can be undone from Batch updates.
 *   Lifting needs a note saying how they agreed, as the API requires.
 */

const ORIGIN_FIELD: Record<FlagField, 'doNotCallSet' | 'doNotContactSet'> = {
  doNotCall: 'doNotCallSet',
  doNotContact: 'doNotContactSet',
};

const ICON: Record<FlagField, React.ReactElement> = {
  doNotCall: <PhoneDisabledOutlinedIcon />,
  doNotContact: <BlockIcon />,
};

interface FlagDialogProps {
  contactId: number;
  field: FlagField;
  /** True marks them; false lifts it. */
  value: boolean;
  onClose: () => void;
  onDone: () => void;
}

export const FlagDialog = ({ contactId, field, value, onClose, onDone }: FlagDialogProps) => {
  const [note, setNote] = useState('');
  const [run, { loading, error }] = useMutation(CRM_SET_CONTACT_FLAG, {
    update: (cache) => forgetBatchData(cache),
  });
  const words = FLAG_WORDS[field];
  const needsNote = flagNeedsNote(value);
  const ready = !needsNote || note.trim().length > 0;

  const submit = async () => {
    try {
      await run({ variables: { filter: { ids: [contactId] }, change: flagChange(field, value, note) } });
      onDone();
    } catch {
      // Shown below from `error`.
    }
  };

  return (
    <Dialog open onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth aria-labelledby="crm-flag-title">
      <DialogTitle id="crm-flag-title">{value ? `Mark ${words.title.toLowerCase()}` : `Lift ${words.title.toLowerCase()}`}</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2 }}>
        <DialogContentText>
          {value
            ? words.means
            : field === 'doNotCall'
              ? 'They can be called again, and the call queue will serve them.'
              : 'They can be called, texted and emailed again, as their other consent allows.'}{' '}
          It is recorded on their timeline and can be undone from Batch updates.
        </DialogContentText>
        <TextField
          label={needsNote ? 'How did they agree?' : 'Why (optional)'}
          placeholder={needsNote ? 'Asked to be called again, on 12 Oct' : 'Asked by email not to be phoned'}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          required={needsNote}
          multiline
          minRows={2}
          inputProps={{ maxLength: 1000 }}
          autoFocus
        />
        {error && <Alert severity="error">{describeBatchError(error)}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={value ? 'error' : 'primary'}
          onClick={submit}
          disabled={!ready || loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {value ? `Mark ${words.title.toLowerCase()}` : 'Lift it'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** The banner across the top of a flagged person's page. */
export const FlagBanners = ({ contact, onChanged }: { contact: ContactDetail; onChanged: () => void }) => {
  const [lifting, setLifting] = useState<FlagField | null>(null);
  const live = contact.mergedIntoId == null;

  return (
    <>
      {shownFlags(contact).map((field) => {
        const origin = contact[ORIGIN_FIELD[field]];
        const words = FLAG_WORDS[field];
        return (
          <Alert
            key={field}
            severity="error"
            variant="filled"
            icon={ICON[field]}
            role="alert"
            aria-label={words.title}
            sx={{
              mb: 2,
              position: 'sticky',
              // Flush with the top of the scroll area: DashboardRoutes' Main pads by 2rem (1rem on
              // phones), and at top: 0 the page would scroll through that strip above the banner.
              top: { xs: '-1rem', sm: '-2rem' },
              zIndex: 3,
              boxShadow: '0 4px 12px rgba(198, 40, 40, 0.25)',
              '& .MuiAlert-message': { minWidth: 0 },
            }}
            action={
              live ? (
                <Button color="inherit" size="small" variant="outlined" onClick={() => setLifting(field)} sx={{ whiteSpace: 'nowrap' }}>
                  Lift…
                </Button>
              ) : undefined
            }
          >
            <AlertTitle sx={{ fontWeight: 700, mb: 0.25 }}>{words.title}</AlertTitle>
            <Typography variant="body2">{words.means}</Typography>
            <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.95, wordBreak: 'break-word' }}>
              {describeFlagOrigin(origin)}
              {origin?.batchId != null && (
                <>
                  {' '}
                  <Link component={RouterLink} to={CRM_PATHS.batchUpdates} color="inherit" underline="always">
                    Batch updates
                  </Link>
                </>
              )}
            </Typography>
            {field === 'doNotContact' && contact.doNotCall && (
              <Typography variant="caption" component="p" sx={{ mt: 0.5 }}>
                Also marked do not call.
              </Typography>
            )}
          </Alert>
        );
      })}
      {lifting && (
        <FlagDialog
          contactId={contact.id}
          field={lifting}
          value={false}
          onClose={() => setLifting(null)}
          onDone={() => {
            setLifting(null);
            onChanged();
          }}
        />
      )}
    </>
  );
};

/** Under the consent gates: mark the person, unless they are marked already. A merged-away record is not changed. */
export const MarkFlags = ({ contact, onChanged }: { contact: ContactDetail; onChanged: () => void }) => {
  const [marking, setMarking] = useState<FlagField | null>(null);
  if (contact.mergedIntoId != null) return null;
  const open = (['doNotCall', 'doNotContact'] as const).filter((field) =>
    // Do not contact already stops calls.
    field === 'doNotCall' ? !contact.doNotCall && !contact.doNotContact : !contact.doNotContact,
  );
  if (open.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
      {open.map((field) => (
        <Button
          key={field}
          size="small"
          color="error"
          variant="outlined"
          startIcon={ICON[field]}
          onClick={() => setMarking(field)}
        >
          Mark {FLAG_WORDS[field].title.toLowerCase()}
        </Button>
      ))}
      {marking && (
        <FlagDialog
          contactId={contact.id}
          field={marking}
          value
          onClose={() => setMarking(null)}
          onDone={() => {
            setMarking(null);
            onChanged();
          }}
        />
      )}
    </Box>
  );
};
